import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { redirect } from "@remix-run/node";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { Toaster } from 'react-hot-toast';

import "~/styles/tailwind.css";
import "~/styles/globals.css";
import Header from "~/components/blocks/Header";
import { SpeedInsights } from "@vercel/speed-insights/remix";
import { getConfig, regionConfigs, requiresCookieConsent, isSupportedRegion } from "custom-config/region.configs";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import Footer from "~/components/blocks/Footer";
import { RegionConfig, useRegionConfig, CurrencyConfig } from "~/store/regionConfigStore";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import { getDeviceInfo, isCrawlerUserAgent, useViewportListener } from "./lib/hooks/useViewportListener";
import { Analytics } from '@vercel/analytics/remix';
import { useFibonacciFormStore, useSessionTracker, useShowFibonacciForm } from "./lib/hooks/useSessionTracker";
import CookieSystem from "./components/cookies/cookie-system";
import { initializeConversionTracking, DATA_LAYER_EVENTS } from "./lib/ConversionTracking";
import { recordJourneyPage } from "./lib/helpers/userJourney";
import { recordTrafficSource } from "./lib/helpers/trafficSource";
import { useClickTracking } from "./lib/hooks/useClickTracking";
import { LoadingProvider } from "./lib/contexts/LoadingContext";
import { GlobalSpinner } from "./components/ui/GlobalSpinner";
import { GlobalLinkInterceptor } from "./components/ui/GlobalLinkInterceptor";
import { UrlChangeManager } from "./components/ui/UrlChangeManager";
import Hotjar from "./components/analytics/Hotjar";
import { useUserLocationStore } from "./store/userLocation";
import { setItem } from "./utils/storage";
import { adClickCaptureScript } from "./lib/helpers/adClickIds";
import { tagAssistantDebugCookieScript } from "./lib/helpers/tagAssistantDebug";
import NotFoundPage from "./components/blocks/NotFoundPage";
import { useChatWidgetStore } from "./store/chatWidgetStore";
import { usePageSectionScroll } from "./lib/hooks/usePageSectionScroll";
import { EmptyLazyFallback, loadLazyModule } from "./lib/helpers/resolveLazyModule";
import RegionMismatchBanner from "./components/blocks/common/RegionMismatchBanner";

const ChatWidget = lazy(() =>
  loadLazyModule(
    () => import("./components/blocks/chatBot/ChatWidget"),
    EmptyLazyFallback,
  ),
);
const UniqueVisitorModal = lazy(() =>
  loadLazyModule(
    () =>
      import("./components/blocks/Modals/UniqueVisitorModal/UniqueVisitorModal").then((mod) =>
        mod?.UniqueVisitorModal ? { default: mod.UniqueVisitorModal } : undefined,
      ),
    EmptyLazyFallback,
  ),
);

function useDeferUntilIdle(timeoutMs = 4000) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(() => setReady(true), { timeout: timeoutMs });
      } else {
        timeoutId = setTimeout(() => setReady(true), timeoutMs);
      }
    };
    if (document.readyState === "complete") {
      arm();
    } else {
      window.addEventListener("load", arm, { once: true });
    }
    return () => {
      window.removeEventListener("load", arm);
      if (idleId != null) window.cancelIdleCallback(idleId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [timeoutMs]);
  return ready;
}



export const links: LinksFunction = () => [
  // Favicon — Google Search requires an explicit icon link element in <head>;
  // serving /favicon.ico at the root alone is not reliably picked up, which is
  // why search results fell back to the generic globe.
  // https://developers.google.com/search/docs/appearance/favicon-in-search
  { rel: "icon", type: "image/x-icon", sizes: "96x96", href: "/favicon.ico" },
  { rel: "shortcut icon", type: "image/x-icon", sizes: "96x96", href: "/favicon.ico" },

  // DNS prefetch for external domains (cheap, no connection overhead)
  { rel: "dns-prefetch", href: "//fonts.googleapis.com" },
  { rel: "dns-prefetch", href: "//fonts.gstatic.com" },
  { rel: "dns-prefetch", href: "//www.googletagmanager.com" },
  { rel: "dns-prefetch", href: "//www.google-analytics.com" },
  { rel: "dns-prefetch", href: "//ik.imagekit.io" },

  // Preconnect — keep ≤4 total to avoid the Lighthouse warning.
  // fonts.googleapis.com + fonts.gstatic.com are required for reliable Inter loading.
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  { rel: "preconnect", href: "https://ik.imagekit.io", crossOrigin: "anonymous" },

  // Preload critical assets
  { rel: "preload", href: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-hub.avif`, as: "image" },

  // Inter must be a real stylesheet in <head>. The previous JS-injected
  // media="print" loader often never flipped to media="all", so the UI
  // stayed on the system fallback font.
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

function parseQueryString(queryString: string): Record<string, string | number> {
  const params = new URLSearchParams(queryString);
  const result: Record<string, string | number> = {};

  params.forEach((value, key) => {
    // Convert numeric values to actual numbers
    result[key] = isNaN(Number(value)) ? value : Number(value);
  });

  return result;
}


export async function loader({ params, request }: LoaderFunctionArgs) {
  const { country } = params;
  const { isMobile, isCrawler } = getDeviceInfo(request);
  // Geo IP is resolved client-side via /api/location so TTFB is not blocked.

  // Handle redirects centrally before any route processing
  const url = new URL(request.url);
  const pathname = url.pathname;

  const { getPathRenameDestination, getUnsupportedRegionDestination } = await import("~/lib/redirects");

  // 301 for legacy directory renames (e.g. `/different-office-types` → `/office-space`).
  // See `app/lib/redirects/pathRenameRedirects.ts` to add more.
  const renamedPath = getPathRenameDestination(pathname);
  if (renamedPath) {
    return redirect(`${renamedPath}${url.search}`, 301);
  }

  // 2-letter prefixes that are not in regionConfigs (e.g. /ab/workspaces) go to the default region page.
  const unsupportedRegionPath = getUnsupportedRegionDestination(pathname);
  if (unsupportedRegionPath) {
    return redirect(`${unsupportedRegionPath}${url.search}`, 301);
  }

  // Remix root loader doesn't receive child route parameters like 'country'.
  // We extract the region code from the URL pathname to ensure the global
  // state (currency, phone, etc.) matches the intended region.
  const urlRegion = pathname.split("/")[1]?.toLowerCase();
  const detectedCountry = (urlRegion && (regionConfigs as any)[urlRegion]) ? urlRegion : (country || DEFAULT_REGION);

  const searchParams = url.searchParams;
  const newParams = parseQueryString(searchParams.toString());

  const cookieHeader = request.headers.get("Cookie");
  const cookies = cookieHeader
    ? Object.fromEntries(
      cookieHeader.split(";").map((c) => {
        const [key, ...v] = c.trim().split("=");
        return [key, decodeURIComponent(v.join("="))];
      }),
    )
    : {};
  const selectedCurrencyCode = cookies.selectedCurrency;

  const regionData = getConfig(detectedCountry as keyof typeof regionConfigs) as RegionConfig | undefined;

  let selectedCurrency = undefined;
  if (selectedCurrencyCode) {
    const config = Object.values(regionConfigs).find((c) => c.currency === selectedCurrencyCode);
    if (config) {
      selectedCurrency = {
        currency: config.currency,
        currencySymbol: config.currencySymbol,
        conversionRate: config.conversionRate,
      };
    }
  }

  // If we are on a regional URL, and the selected currency from cookie doesn't match
  // the region's default, we prioritize the region's default to allow region-switching 
  // to reset the currency.
  const isExplicitRegion = !!(urlRegion && (regionConfigs as any)[urlRegion]);
  const finalSelectedCurrency = isExplicitRegion && selectedCurrency?.currency !== regionData?.currency
    ? undefined
    : selectedCurrency;

  const currency = finalSelectedCurrency?.currency || regionData?.currency;
  const chatbotEnabledValue = (process.env.PUBLIC_REMIX_ENABLE_CHATBOT || "").trim().toLowerCase();
  const chatbotEnabled = chatbotEnabledValue === "true" || chatbotEnabledValue === "1" || chatbotEnabledValue === "yes";
  const allowedChatbotRegions = (process.env.PUBLIC_REMIX_CHATBOT_REGIONS || "")
    .split(",")
    .map((code) => code.trim().toLowerCase())
    .filter(Boolean);
  const allowAllChatbotRegions =
    allowedChatbotRegions.includes("all") || allowedChatbotRegions.includes("*");
  const showChatbot = chatbotEnabled
    && (allowAllChatbotRegions || allowedChatbotRegions.length === 0 || allowedChatbotRegions.includes(detectedCountry.toLowerCase()));

  const imageKitUrl = process.env.PUBLIC_REMIX_APP_IMAGEKIT_URL_ENDPOINT;

  // GDPR-style regions must start with consent denied until the user accepts;
  // everywhere else GTM/UET fire with consent granted by default.
  const cookieConsentRequired = requiresCookieConsent(detectedCountry);

  return {
    imageKitUrl,
    cookieConsentRequired,
    ENV: {
      PUBLIC_REMIX_GOOGLE_MAP: process.env.PUBLIC_REMIX_GOOGLE_MAP,
      PUBLIC_REMIX_GOOGLE_TAG_MANAGER: process.env.PUBLIC_REMIX_GOOGLE_TAG_MANAGER,
      PUBLIC_REMIX_GA4_MEASUREMENT_ID: process.env.PUBLIC_REMIX_GA4_MEASUREMENT_ID,
      PUBLIC_REMIX_HOTJAR_SITE_ID: process.env.PUBLIC_REMIX_HOTJAR_SITE_ID,
      PUBLIC_REMIX_APP_IMAGEKIT_URL_ENDPOINT: process.env.PUBLIC_REMIX_APP_IMAGEKIT_URL_ENDPOINT,
      PUBLIC_REMIX_APP_NEW_GOOGLE_TAG_MANAGER: process.env.PUBLIC_REMIX_APP_NEW_GOOGLE_TAG_MANAGER,
      PUBLIC_REMIX_APP_MAPBOX_ACCESS_TOKEN: process.env.PUBLIC_REMIX_APP_MAPBOX_ACCESS_TOKEN,
      PUBLIC_REMIX_APP_MAPBOX_STYLE: process.env.PUBLIC_REMIX_APP_MAPBOX_STYLE,
      PUBLIC_REMIX_ENABLE_CHATBOT: process.env.PUBLIC_REMIX_ENABLE_CHATBOT,
      PUBLIC_REMIX_CHATBOT_REGIONS: process.env.PUBLIC_REMIX_CHATBOT_REGIONS,
      // prod link
      IS_PRODUCTION: process.env.NODE_ENV === "production",
    },
    showChatbot,
    regionData,
    currency,
    selectedCurrency: finalSelectedCurrency,
    country: detectedCountry,
    params: newParams,
    isMobile,
    isCrawler,
    location: null,
  };
}

export function Layout({ children }: { children: React.ReactNode }) {
  const shouldShow = useShowFibonacciForm()
  const { markCancelled, markCompleted } = useFibonacciFormStore()
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
  const data = useRouteLoaderData<typeof loader>("root");
  const location = useLocation();
  const isChatOpen = useChatWidgetStore((state) => state.isChatOpen);
  usePageSectionScroll();
  const deferChrome = useDeferUntilIdle(4000);
  const isProduction = Boolean(data?.ENV?.IS_PRODUCTION);
  const isCrawler = Boolean(data?.isCrawler);
  const allowMarketingTags = isProduction && !isCrawler && deferChrome;

  // Close mobile Header Sheet when UniqueVisitorModal opens so modal inputs (e.g. budget) can receive focus
  useEffect(() => {
    if (shouldShow) setMobileSheetOpen(false)
  }, [shouldShow])

  // GTM (+ UET via GTM) is an inline <head> snippet — see the Layout markup below.

  // Defer Pardot until idle (humans only).
  useEffect(() => {
    if (!allowMarketingTags) return;
    if (typeof document === "undefined") return;
    if (document.getElementById("pardot-pd-js")) return;

    const cfg = document.createElement("script");
    cfg.id = "pardot-config";
    cfg.text = `piAId = "549422"; piCId = "20879";`;
    document.head.appendChild(cfg);

    const pd = document.createElement("script");
    pd.id = "pardot-pd-js";
    pd.async = true;
    pd.src = "https://pi.pardot.com/pd.js";
    document.head.appendChild(pd);
  }, [allowMarketingTags]);

  // Google tag (gtag.js) programmatic injection + SPA pageview tracking
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!allowMarketingTags) return;
    const measurementId = data?.ENV?.PUBLIC_REMIX_APP_NEW_GOOGLE_TAG_MANAGER;
    if (!measurementId) return;

    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
      w.gtag = function gtag() {
        w.dataLayer.push(arguments);
      };
    }

    // Inject the loader script once.
    if (!document.getElementById("gtag-loader")) {
      const loader = document.createElement("script");
      loader.id = "gtag-loader";
      loader.async = true;
      loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      document.head.appendChild(loader);
    }

    // Initialize once.
    if (!w.__OH_GTAG_INITIALIZED__) {
      w.__OH_GTAG_INITIALIZED__ = true;
      w.gtag("js", new Date());
      w.gtag("config", measurementId);
    }

    // Track each client-side navigation as a page view.
    w.gtag("config", measurementId, {
      page_path: `${location.pathname}${location.search}`,
    });
  }, [
    allowMarketingTags,
    data?.ENV?.PUBLIC_REMIX_APP_NEW_GOOGLE_TAG_MANAGER,
    location.pathname,
    location.search,
  ]);



  const { updateRegionConfig, updateSelectedCurrency } = useRegionConfig();
  const { setLocation } = useUserLocationStore();

  // Geo is optional chrome. Defer until idle and skip crawlers so a blocked
  // request cannot contend with hydrate or become a Remix route error.
  useEffect(() => {
    if (data?.location) {
      setLocation(data.location as any);
      return;
    }
    if (typeof window === "undefined") return;
    if (isCrawler || !deferChrome) return;
    if (isCrawlerUserAgent(window.navigator.userAgent)) return;

    let cancelled = false;
    fetch("/api/location")
      .then((response) => (response.ok ? response.json() : null))
      .then((location) => {
        if (!cancelled && location) {
          setLocation(location as any);
        }
      })
      .catch(() => {
        // Intentionally ignored — geo lookup must never crash the tree.
      });

    return () => {
      cancelled = true;
    };
  }, [data?.location, setLocation, isCrawler, deferChrome]);

  useEffect(() => {
    if (data?.regionData !== undefined) {
      const regionConfig = data?.regionData as RegionConfig;
      updateRegionConfig(regionConfig);
    }
    if (data?.selectedCurrency) {
      updateSelectedCurrency(data.selectedCurrency as CurrencyConfig);
    }
  }, [data?.regionData, data?.selectedCurrency]);


  useViewportListener();
  useSessionTracker();

  // Store keyword parameter in localStorage if present
  useEffect(() => {
    if (typeof window !== "undefined" && data?.params) {
      const keyword = data.params.keyword;
      if (keyword) {
        // Convert to string if it's a number, otherwise use as string
        const keywordString = typeof keyword === "string" ? keyword : String(keyword);
        if (keywordString.trim()) {
          setItem("ppcKeywords", keywordString);
        }
      }
    }
  }, [data?.params]);

  const handleCancel = () => {

    markCancelled()

  }

  const handleSubmit = () => {
    markCompleted()
  }

  // URL-derived tracking params (device / keywords / campaign) consumed
  // by UniqueVisitorModal. Source: loader-returned `params`.
  const modalParams = (data?.params ?? {}) as Record<string, string | undefined>;

  // Initialize automatic conversion tracking after idle (never for crawlers).
  useEffect(() => {
    if (!allowMarketingTags) return;
    initializeConversionTracking();
  }, [allowMarketingTags]);

  // Phone / WhatsApp / Add Listing clicks, from one delegated listener.
  useClickTracking(!isCrawler);

  // Visitor journey. One hook classifies every route by its path, which is why
  // no listing, search or property component has to know the journey exists.
  // Records nothing without a visitor ID, i.e. without functional consent.
  useEffect(() => {
    if (isCrawler) return;
    const update = recordJourneyPage(location.pathname);
    if (!update) return;

    // document.referrer survives for the life of the document, so the source can
    // be classified here rather than in the inline <head> script.
    const traffic = recordTrafficSource();

    window.dataLayer = window.dataLayer || [];
    // Variables on every page: readable by every tag, firing none of them.
    window.dataLayer.push({
      ...update.summary,
      ...(traffic
        ? {
          traffic_source: traffic.source,
          traffic_channel: traffic.channel,
          traffic_referrer: traffic.referrer,
        }
        : {}),
    });
    // The event fires once per session, never per page and never per click.
    if (update.newSession) {
      window.dataLayer.push({
        event: DATA_LAYER_EVENTS.USER_JOURNEY,
        ...update.summary,
      });
    }
  }, [location.pathname, isCrawler]);

  // Store campaign data from URL parameters in localStorage
  useEffect(() => {
    if (typeof window !== "undefined" && data?.params) {
      const params = data.params;
      // Ad tracking templates send campaign/location/workspace/country with a `utm_` prefix.
      const campaignParams = {
        keyword: params.keyword || params.keywords || "",
        network: params.network || "",
        device: params.device || "",
        campaign: params.campaign || params.utm_campaign || "",
        location: params.location || params.utm_location || "",
        workspace: params.workspace || params.utm_workspace || "",
        country: params.country || params.utm_country || "",
      };
      // testing

      // Only store if at least one campaign parameter exists
      const hasCampaignData = Object.values(campaignParams).some(value => value !== "");
      if (hasCampaignData) {
        setItem("campaignData", campaignParams);
      }
    }
  }, [data?.params]);

  // let isSinglePardotForm = isSinglePardotFormAllowed(data?.country || '');

  // Campaign route only: /office-spaces or /:country/office-spaces (not blog slugs containing "office-spaces")
  const isCampaignPage = location.pathname
    .split("/")
    .filter(Boolean)
    .includes("office-spaces");

  // Homepage only: `/` or `/{region}` (e.g. `/au`)
  const isHomePage = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return true;
    return segments.length === 1 && isSupportedRegion(segments[0]);
  }, [location.pathname]);

  const htmlLang = data?.regionData?.langCode || "en";

  return (
    <html lang={htmlLang}>
      <head>
        {/* Consent Mode defaults first in <head> (must precede GTM), then the GTM
            snippet itself so gtm.js is requested during HTML parse; gtag/Pardot
            loaders after idle for humans; crawlers never receive marketing tags. */}
        {isProduction && !isCrawler && (
          <>
            {/* Google Consent Mode v2 defaults — MUST run before the GTM snippet.
                Non-EU/UK/CH regions default to granted; consent-required regions
                default to denied until the user accepts via the cookie banner. */}
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `(function(){
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
var consentRequired = ${JSON.stringify(Boolean(data?.cookieConsentRequired))};
var granted = { ad_storage:'granted', ad_user_data:'granted', ad_personalization:'granted', analytics_storage:'granted', functionality_storage:'granted', personalization_storage:'granted', security_storage:'granted' };
var denied = { ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied', analytics_storage:'denied', functionality_storage:'denied', personalization_storage:'denied', security_storage:'granted' };
try {
  var stored = localStorage.getItem('cookieConsent');
  if (stored) {
    var prefs = (JSON.parse(stored).preferences) || {};
    var adState = prefs.advertising ? 'granted' : 'denied';
    var fnState = prefs.functional ? 'granted' : 'denied';
    gtag('consent', 'default', {
      ad_storage: adState,
      ad_user_data: adState,
      ad_personalization: adState,
      analytics_storage: fnState,
      functionality_storage: fnState,
      personalization_storage: fnState,
      security_storage: 'granted'
    });
  } else {
    gtag('consent', 'default', consentRequired ? denied : granted);
  }
} catch (e) {
  gtag('consent', 'default', consentRequired ? denied : granted);
}
})();`,
              }}
            />
            {/* Microsoft UET consent seed — bat.js still loads via GTM. */}
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `
                  window.uetq = window.uetq || [];
                  (function() {
                    var consentRequired = ${JSON.stringify(Boolean(data?.cookieConsentRequired))};
                    var defaultState = consentRequired ? 'denied' : 'granted';
                    try {
                      var cookieConsent = localStorage.getItem('cookieConsent');
                      if (cookieConsent) {
                        var consent = JSON.parse(cookieConsent);
                        var adGranted = !!(consent.preferences && consent.preferences.advertising === true);
                        window.uetq.push('consent', 'default', {
                          'ad_storage': 'denied'
                        });
                        if (adGranted) {
                          window.uetq.push('consent', 'update', {
                            'ad_storage': 'granted'
                          });
                        }
                      } else {
                        window.uetq.push('consent', 'default', {
                          'ad_storage': defaultState
                        });
                      }
                    } catch (e) {
                      window.uetq.push('consent', 'default', {
                        'ad_storage': defaultState
                      });
                    }
                  })();
                `,
              }}
            />
            {/* Tag Assistant: keeps a debug session alive across target="_blank"
                navigations. The cookie must exist before gtm.js runs. */}
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{ __html: tagAssistantDebugCookieScript() }}
            />
            {/* Google Tag Manager (GTM-PTHJV3S) — loads first, right after the consent defaults. */}
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-PTHJV3S');`,
              }}
            />
          </>
        )}
        {/* Ad click IDs (msclkid/gclid) captured before hydration so navigating away cannot lose them. */}
        {!isCrawler && (
          <script
            suppressHydrationWarning
            dangerouslySetInnerHTML={{
              __html: adClickCaptureScript(Boolean(data?.cookieConsentRequired)),
            }}
          />
        )}
        {!data?.ENV?.IS_PRODUCTION && (
          <meta name="robots" key="robots" content="noindex, nofollow" />
        )}
        {data?.regionData?.indexable !== true && (
          <meta name="robots" key="robots-indexable" content="noindex, follow" />
        )}
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover' />
        {/* Google tag (gtag.js) injected client-side in Layout (SPA-safe) */}

        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `
       // Set screen width and component preference
      (function() {
   
        // Suppress all console logs and errors in production
        // console.log('window.location.hostname', window.location.hostname)
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          console.log = function() {};
          console.error = function() {};
          console.warn = function() {};
          console.info = function() {};
          console.debug = function() {};
          window.onerror = function() { return true; }; // Prevents error alerts
        }
      })();
    `,
          }}
        />
        {/* Google Analytics 4  G-VE6CVQLW1X*/}
        {/* End Google Analytics 4 */}

        <Meta />
        <Links />


      </head>
      <body className='min-h-screen bg-background'>
        {isProduction && !isCrawler ? (
          <noscript key="gtm-noscript">
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-PTHJV3S"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        ) : null}
        {/* End Google Tag Manager (noscript) */}
        {allowMarketingTags ? <Analytics /> : null}
        {allowMarketingTags ? <Hotjar siteId={data?.ENV?.PUBLIC_REMIX_HOTJAR_SITE_ID || ''} /> : null}
        <Toaster />
        {!isCampaignPage && (
          <div className='sticky top-0 z-20 bg-white'>
            <Header
              isMobile={Boolean(data?.isMobile)}
              region={data?.country}
              config={data?.regionData}
              location={data?.location}
              mobileSheetOpen={mobileSheetOpen}
              setMobileSheetOpen={setMobileSheetOpen}
            />
            {!isCrawler && isHomePage ? (
              <RegionMismatchBanner pageRegion={data?.country} />
            ) : null}
          </div>
        )}
        {!isCampaignPage && shouldShow && deferChrome && !isCrawler && !isChatOpen ? (
          <Suspense fallback={null}>
            <UniqueVisitorModal
              visible
              params={modalParams}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              region={data?.country}
            />
          </Suspense>
        ) : null}
        {children}
        {deferChrome && data?.showChatbot && !isCrawler && !/\/propertyGPT\b|\/property-gpt\b/i.test(location.pathname) ? (
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        ) : null}
        {!isCrawler ? <CookieSystem isMobile={Boolean(data?.isMobile)} /> : null}
        {!isCampaignPage && (
          <Footer
            region={data?.country ?? DEFAULT_REGION}
            isMobile={Boolean(data?.isMobile)}
            config={data?.regionData}
            ipLocation={data?.location}
          />
        )}

        <ScrollRestoration />
        <Scripts />
        {/* Top layer for modals/drawers (above Sheet z-40): UniqueVisitorModal, cookie drawer, etc. */}
        <div
          id="drawer-portal-root"
          className="fixed inset-0 z-[55] pointer-events-none"
          aria-hidden="true"
        />
        {/* End Google Analytics 4 Configuration */}
      </body>
      {allowMarketingTags ? <SpeedInsights /> : null}
    </html >
  );
}
// testing
function App() {
  return (
    <LoadingProvider>
      <Outlet />
      <GlobalSpinner />
      <GlobalLinkInterceptor />
      <UrlChangeManager />
    </LoadingProvider>
  );
}

// Wrap with Sentry in production only. `withSentry` adds router instrumentation
// + automatic error capture and should never fire from local/dev builds.
const sentryEnabled = process.env.NODE_ENV === "production";
const AppWithSentry = sentryEnabled ? withSentry(App) : App;
export default AppWithSentry;

export function ErrorBoundary() {
  const error = useRouteError();
  const isDevelopment = process.env.NODE_ENV !== "production";

  // Always forward to Sentry when Sentry is active (prod, or dev-enabled).
  // Skip 4xx route responses — those are usually expected (e.g. 404) and
  // would just be noise in the dashboard.
  if (sentryEnabled) {
    const isRouteResp = isRouteErrorResponse(error);
    const shouldCapture = !isRouteResp || error.status >= 500;
    if (shouldCapture) {
      captureRemixErrorBoundaryError(error);
    }
  }

  if (!isDevelopment) {
    return <NotFoundPage />;
  }

  const isRouteError = isRouteErrorResponse(error);
  const errorObj = error as Error | undefined;

  return (
    <html lang='en'>
      <head>
        <Meta />
        <Links />
      </head>
      <body className='min-h-screen bg-background p-6'>
        <div className='mx-auto max-w-4xl rounded-lg border border-red-300 bg-red-50 p-5 text-red-900'>
          <h1 className='mb-3 text-xl font-bold'>
            {isRouteError ? "Route Error" : "Application Error"}
          </h1>
          {isRouteError ? (
            <>
              <p className='mb-2'>
                <strong>Status:</strong> {error.status} {error.statusText}
              </p>
              <pre className='overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-sm'>
                {typeof error.data === "string"
                  ? error.data
                  : JSON.stringify(error.data, null, 2)}
              </pre>
            </>
          ) : (
            <>
              <p className='mb-2'>
                <strong>Message:</strong> {errorObj?.message ?? "Unknown error"}
              </p>
              {errorObj?.stack && (
                <pre className='overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs'>
                  {errorObj.stack}
                </pre>
              )}
            </>
          )}
        </div>
        <Scripts />
      </body>
    </html>
  );
}
